import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  IdentificationIcon,
  UserGroupIcon,
  AtSymbolIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  MagnifyingGlassCircleIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import VAsTab from './admin/VAsTab';

const AdminVAs = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      toast.error('Admin access required');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="space-y-6 -mx-4 sm:-mx-6 lg:-mx-8">
      {/* Hero */}
      <div className={`relative overflow-hidden rounded-none sm:rounded-2xl mx-4 sm:mx-6 lg:mx-8 ${
        isDarkMode
          ? 'bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900'
          : 'bg-gradient-to-br from-indigo-50 via-violet-50 to-slate-50'
      }`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute -top-20 right-1/4 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-indigo-600/20' : 'bg-indigo-300/40'
          }`} />
          <div className={`absolute -bottom-16 left-1/4 w-64 h-64 rounded-full blur-3xl ${
            isDarkMode ? 'bg-violet-600/15' : 'bg-violet-200/50'
          }`} />
        </div>
        <div className="relative px-6 sm:px-8 py-8 sm:py-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-start gap-5">
              <div className={`flex-shrink-0 flex items-center justify-center w-14 h-14 rounded-2xl ${
                isDarkMode
                  ? 'bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/30'
                  : 'bg-gradient-to-br from-indigo-500 to-violet-500 shadow-lg shadow-indigo-400/30'
              }`}>
                <IdentificationIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className={`text-2xl sm:text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Virtual Assistants
                </h1>
                <p className={`mt-1.5 text-sm sm:text-base max-w-xl ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Manage VA users, target username pools, GoLogin accounts, portal content, and auto-discovery feeds.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:items-end gap-3">
              <Link
                to="/va"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-lg shadow-indigo-500/25 transition-all"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                Open VA Portal
              </Link>
              <p className={`text-xs text-right max-w-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Preview the portal as admin, or use <strong>Open as</strong> on a VA user to see their exact view.
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'VA Users', icon: UserGroupIcon },
              { label: 'Target pool', icon: AtSymbolIcon },
              { label: 'Accounts', icon: ClipboardDocumentListIcon },
              { label: 'Performance', icon: ChartBarIcon },
              { label: 'Portal config', icon: Cog6ToothIcon },
              { label: 'Auto discovery', icon: MagnifyingGlassCircleIcon }
            ].map((item) => (
              <div
                key={item.label}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium ${
                  isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-white/70 text-slate-600'
                }`}
              >
                <item.icon className="w-4 h-4 opacity-70" />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8">
        <VAsTab />
      </div>
    </div>
  );
};

export default AdminVAs;
